import { Button, Center, Flex, Grid, Stack, Switch, Title } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import { useMemo } from 'react';
import { z } from 'zod';

import { Forwarder } from '@e-origin/shared';

import { IconFactory } from '@assets';
import { IconBox, SelectAddToList, StickyContainer } from '@components';
import { useCustomers } from '@hooks/customer';
import { useUpdateForwarder } from '@hooks/forwarder';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';
import { useTemplates } from '@hooks/useTemplates';

const apiAccessSchema = z.object({
  apiAccess: z.boolean(),
  selectedCustomers: z.array(z.string()).optional().default([]),
  selectedTemplates: z.array(z.string()).optional().default([]),
});

type ApiAccessFormValues = z.infer<typeof apiAccessSchema>;

interface ApiAccessParams {
  readonly?: boolean;
  forwarder?: Forwarder;
}

export const ApiAccess: React.FC<ApiAccessParams> = ({ readonly, forwarder }) => {
  const { data: { list: customers } = { list: [] } } = useCustomers();
  const { data: templates = [] } = useTemplates();

  const updateForwarder = useUpdateForwarder();

  const customersList = useMemo(
    () =>
      customers.map((customer) => ({
        label: customer.name,
        value: customer._id,
      })),
    [customers],
  );

  const templatesList = useMemo(
    () =>
      templates.map((template) => ({
        label: template.name,
        value: template._id,
      })),
    [templates],
  );

  const form = useFormWithNameInputProp<ApiAccessFormValues>({
    initialValues: {
      apiAccess: forwarder?.apiAccess || false,
      selectedCustomers: forwarder?.customers || [],
      selectedTemplates: forwarder?.templates || [],
    },
    validate: zodResolver(apiAccessSchema),
    validateInputOnBlur: true,
  });

  const onSubmit = (values: ApiAccessFormValues) => {
    if (!values.apiAccess) {
      form.setInitialValues({
        apiAccess: false,
        selectedCustomers: [],
        selectedTemplates: [],
      });
    }

    updateForwarder.mutate({
      body: {
        _id: forwarder?._id,
        apiAccess: values.apiAccess,
        customers: values.apiAccess ? values.selectedCustomers : [],
        templates: values.apiAccess ? values.selectedTemplates : [],
      },
    });
  };

  const { apiAccess } = form.values;

  const enableLabel = useMemo(() => {
    if (readonly) return apiAccess ? "You have access to broker's API" : "You don't have access to broker's API";
    return `API access ${apiAccess ? 'enabled' : 'disabled'} `;
  }, [readonly, apiAccess]);

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack>
        {!readonly && <Switch label={enableLabel} {...form.getInputProps('apiAccess', { type: 'checkbox' })} />}
        {readonly && (
          <>
            {apiAccess && (
              <Flex gap={8}>
                <IconBox icon={IconFactory.released} />
                {enableLabel}
              </Flex>
            )}
            {!apiAccess && (
              <Center mih="calc(100vh - 400px)">
                <Flex gap={8}>
                  <IconBox icon={IconFactory.notReleased} />
                  {enableLabel}
                </Flex>
              </Center>
            )}
          </>
        )}

        {apiAccess && (
          <Grid>
            <Grid.Col span={4} h="calc(100vh - 420px)">
              <Title order={3} mb="sm">
                Customers
              </Title>
              <SelectAddToList
                {...form.getInputProps('selectedCustomers')}
                options={customersList}
                label="Select customers..."
                disabled={!apiAccess}
                readonly={readonly}
                size="lg"
              />
            </Grid.Col>
            <Grid.Col span={4} h="calc(100vh - 420px)">
              <Title order={3} mb="sm">
                Templates
              </Title>
              <SelectAddToList
                {...form.getInputProps('selectedTemplates')}
                options={templatesList}
                label="Select templates..."
                disabled={!apiAccess}
                readonly={readonly}
                size="lg"
              />
            </Grid.Col>
          </Grid>
        )}

        {!readonly && (
          <StickyContainer noPl>
            <Button type="submit" size="lg" loading={updateForwarder.isPending} disabled={!form.isDirty()}>
              Save API access
            </Button>
          </StickyContainer>
        )}
      </Stack>
    </form>
  );
};
